/**
 * The settlement networks this build can serve, in one place.
 *
 * The selector, the router and the prover gate read from here so that adding
 * a network is a registry entry plus its app, not a hunt through the tree.
 * EVM configuration stays in `src/config`; Solana in `src/config/solana`.
 */
import {
  BASE_DEPLOYMENT_CHAIN_ID,
  DOGEOS_CHIKYU,
  EVM_CONFIG,
  isProductionChainId,
} from "../config";
import { EVM_NETWORK_DEFINITIONS } from "../config/evmNetworks";
import type { DogeOsChikyuConfig } from "../config/evmNetworks";
import { SOLANA_NETWORKS, type SolanaNetworkConfig } from "../config/solana";

import { STELLAR_V2_ENABLED, STELLAR_V2_ISOLATED } from "../config/stellar";

export type SettlementNetworkKind = "evm" | "solana" | "stellar";

export interface SettlementNetwork {
  /** Stable identifier used in storage namespaces and payment links. */
  key: string;
  kind: SettlementNetworkKind;
  label: string;
  description: string;
  /** Path the network's app is mounted at. */
  route: string;
  enabled: boolean;
  /** Real value at stake: development artifacts are never acceptable. */
  production: boolean;
  /** Why the network is listed but not usable, when it is not. */
  disabledReason?: string;
}

function evmSettlementNetwork(): SettlementNetwork {
  // The Base deployment's chain, whichever page is being served.
  const chainId = BASE_DEPLOYMENT_CHAIN_ID ?? EVM_CONFIG.CHAIN_ID;
  const definition = EVM_NETWORK_DEFINITIONS[chainId];
  const named =
    definition && definition.key !== "dogeos-chikyu"
      ? { key: definition.key, label: definition.label }
      : {
          key: `evm-${chainId}`,
          label: `EVM chain ${chainId}`,
        };
  return {
    ...named,
    kind: "evm",
    description: "EVM privacy operations.",
    route: "/base",
    enabled: true,
    production: isProductionChainId(chainId),
  };
}

/**
 * DogeOS Chikyu is listed in every build and served only when its pool is
 * configured; a malformed variable disables it with the reason shown.
 */
function dogeOsSettlementNetwork(
  config: DogeOsChikyuConfig,
): SettlementNetwork {
  return {
    key: config.key,
    kind: "evm",
    label: config.label,
    description:
      "DOGE privacy operations on the DogeOS Chikyu testnet; testnet DOGE carries no value.",
    route: config.route,
    enabled: config.enabled,
    production: false,
    disabledReason: config.enabled
      ? undefined
      : (config.configurationError ?? "Not configured in this deployment."),
  };
}

function solanaSettlementNetwork(
  config: SolanaNetworkConfig,
): SettlementNetwork {
  const production = config.cluster === "mainnet-beta";
  return {
    key: config.key,
    kind: "solana",
    label: config.label,
    description: production
      ? "Solana privacy operations."
      : "Solana privacy operations on a test cluster; devnet tokens carry no value.",
    route: config.route,
    enabled: config.enabled,
    production,
    disabledReason: config.enabled
      ? undefined
      : (config.configurationError ?? "Not configured in this deployment."),
  };
}

export function listSettlementNetworks(): SettlementNetwork[] {
  if (STELLAR_V2_ISOLATED)
    return [
      {
        key: "stellar-testnet",
        kind: "stellar",
        label: "Stellar Testnet",
        route: "/stellar",
        enabled: true,
        production: false,
        description:
          "Vanta V2 verification on Stellar Testnet. No financial custody.",
      },
    ];
  return [
    evmSettlementNetwork(),
    dogeOsSettlementNetwork(DOGEOS_CHIKYU),
    ...SOLANA_NETWORKS.map(solanaSettlementNetwork),
    ...(STELLAR_V2_ENABLED
      ? [
          {
            key: "stellar-testnet",
            kind: "stellar" as const,
            label: "Stellar Testnet",
            route: "/stellar",
            enabled: true,
            production: false,
            description:
              "Private XLM payments on Stellar Testnet. Faucet funds only.",
          },
        ]
      : []),
  ];
}

export function getSettlementNetwork(
  key: string,
): SettlementNetwork | undefined {
  return listSettlementNetworks().find((network) => network.key === key);
}

/**
 * The network an app path belongs to. Each app answers its mount path and
 * its `/send` payment-link path; anything else is not an app route.
 */
export function settlementNetworkForPath(
  pathname: string,
): SettlementNetwork | undefined {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return listSettlementNetworks().find(
    (network) =>
      normalized === network.route || normalized === `${network.route}/send`,
  );
}

/**
 * Whether this build needs the browser Groth16 prover: the flat V2 EVM stack
 * (on Base or DogeOS Chikyu) or any Solana network. The prover artifacts
 * are network independent.
 */
export function flatJoinSplitV2ProverRequired(): boolean {
  if (STELLAR_V2_ISOLATED) return false;
  return (
    EVM_CONFIG.JOINSPLIT_V2.ENABLED ||
    DOGEOS_CHIKYU.enabled ||
    SOLANA_NETWORKS.some((network) => network.enabled)
  );
}
